'use client';
import { useEffect, useRef, useState } from 'react';
import { ITabbedChartModularBlock } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import { tabbedBarChartVariants, tabbedBarChartConfig } from './TabbedBarChart.styles';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';

type ChartDetails = NonNullable<
  NonNullable<ITabbedChartModularBlock['tabbed_chart_item']>[number]['chart_details']
>;

// Greedy word-wrap: splits `text` into lines fitting `maxWidth` pixels at `fontSize`.
const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
  const avgCharWidth = fontSize * 0.6;
  const maxChars = Math.floor(maxWidth / avgCharWidth);
  if (maxChars <= 0 || text.length <= maxChars) return [text];

  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [text];
};

export const TabbedBarChart = ({ chartDetails }: { chartDetails: ChartDetails }) => {
  const { title, note, chartWrapper, barRow, valueLabel, barWrapper, bar } =
    tabbedBarChartVariants();
  const {
    yAxisWidth,
    fallbackColor,
    label,
    tick,
    animation,
    minBarHeight,
    lineHeight,
    barGap,
    domainPadding,
  } = tabbedBarChartConfig;

  const [animated, setAnimated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const items = chartDetails.chart_items ?? [];
  const data = [...items]
    .map((item) => ({
      name: item.item_title ?? '',
      displayValue: item.item_value ?? '',
      value: parseFloat(item.item_value ?? '0') || 0,
      color: item.item_color?.color_list ?? fallbackColor,
      $: item.$,
    }))
    .sort((a, b) => b.value - a.value);

  if (!data.length) return null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const barAreaWidth = Math.max(0, containerWidth - yAxisWidth);

  // Pre-compute final bar pixel widths and wrap text at those widths.
  // Heights are fixed before animation so they don't shift as bars grow.
  const barMeta = data.map((item) => {
    const barPx = barAreaWidth > 0 ? (item.value / maxValue) * domainPadding * barAreaWidth : 0;
    const textPx = Math.max(0, barPx - label.paddingX * 2);
    const lines = barAreaWidth > 0 ? wrapText(item.name, textPx, label.fontSize) : [item.name];
    const lhPx = label.fontSize * lineHeight;
    const height = Math.max(minBarHeight, lines.length * lhPx + label.paddingY * 4);
    const widthPct = (item.value / maxValue) * domainPadding * 100;
    return { lines, height, widthPct };
  });

  return (
    <figure aria-label={chartDetails.chart_title ?? 'Bar chart'} ref={containerRef}>
      {chartDetails.chart_title && (
        <figcaption className={title()} {...getCSLPAttributes(chartDetails.$?.chart_title)}>
          {chartDetails.chart_title}
        </figcaption>
      )}
      <div className={chartWrapper()} style={{ gap: barGap }}>
        {data.map((item, i) => {
          const { lines, height, widthPct } = barMeta[i];
          return (
            <div key={i} className={barRow()}>
              {/* Value label */}
              <div
                className={valueLabel()}
                style={{
                  width: yAxisWidth,
                  fontSize: tick.fontSize,
                  color: tick.color,
                  fontWeight: tick.fontWeight,
                }}
                {...getCSLPAttributes(item.$?.item_value)}
              >
                {item.displayValue}
              </div>

              {/* Bar */}
              <div className={barWrapper()}>
                <div
                  className={bar()}
                  {...getCSLPAttributes(item.$?.item_title)}
                  style={{
                    height,
                    width: animated ? `${widthPct}%` : '0%',
                    backgroundColor: item.color,
                    transition: `width ${animation.barDuration} ${animation.easing}`,
                    display: 'flex',
                    alignItems: 'center',
                    padding: `${label.paddingY}px ${label.paddingX}px`,
                  }}
                  role="graphics-symbol"
                  aria-roledescription="bar"
                  aria-label={`${item.name}: ${item.displayValue}`}
                >
                  <span
                    style={{
                      color: label.color,
                      fontSize: label.fontSize,
                      fontWeight: label.fontWeight,
                      lineHeight,
                      opacity: animated ? 1 : 0,
                      transition: `opacity ${animation.labelDuration} ${animation.easing} ${animation.labelDelay}`,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {lines.join('\n')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {chartDetails.chart_note && (
        <RichTextWrapper
          content={chartDetails.chart_note}
          className={note()}
          {...getCSLPAttributes(chartDetails.$?.chart_note)}
        />
      )}
    </figure>
  );
};
